/**
 * The Recognition contract — one flat router, `recognitionContract` (`/recognition`), covering the
 * award catalogue, the kudos/points wall, the effective-dated points ledger, and the leaderboard. See
 * the Feedback & 1:1s plan's "Contract & API" and "Data model" sections.
 *
 * POINTS ARE `Int`, NEVER `Decimal` — the one deliberate exception to CLAUDE.md invariant 2 in this
 * whole module. They are social-only in v1, not redeemable, so there is no currency to pair them
 * with; see `RecognitionPointsBalance`'s doc comment in schema.prisma for what changes if a
 * redeemable points economy lands later.
 *
 * `getLeaderboard` HAS NO K-ANONYMITY SUPPRESSION, DELIBERATELY. Section 5's analytics rule (suppress
 * small cohorts to protect individual privacy) does not apply here: a leaderboard is voluntary public
 * attribution someone opted into by receiving a public kudos, not private appraisal data. Don't "fix"
 * this to match Section 5 — see routes/recognition.ts's header for the same note at the enforcement
 * point.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  RECOGNITION_VISIBILITIES,
  RECOGNITION_LEDGER_ENTRY_TYPES,
  REWARD_REDEMPTION_STATUSES,
} from '../enums.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const recognitionVisibilitySchema = z.enum(RECOGNITION_VISIBILITIES);

/** Points — a plain non-negative int, never a decimal string (see this file's header). */
/** Upper bound stops a typo or a crafted request from overflowing the 32-bit Int column. */
export const pointsSchema = z.number().int().nonnegative().max(100000);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const recognitionAwardSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  points: pointsSchema,
  badgeIcon: z.string().nullable(),
  isActive: z.boolean(),
});
export type RecognitionAward = z.infer<typeof recognitionAwardSchema>;

/** The kudos facts a printed certificate shows. Free text here was already DLP-scanned at send. */
export const recognitionCertificateSummarySchema = z.object({
  fromName: z.string(),
  toName: z.string(),
  message: z.string(),
  points: pointsSchema,
  awardName: z.string().nullable(),
  createdAt: instantSchema,
});
export type RecognitionCertificateSummary = z.infer<typeof recognitionCertificateSummarySchema>;

/**
 * A printable certificate of appreciation for one kudos. Issued on request only; issuing again
 * returns the same certificate, never a second number. `certificateNumber` is `REC-<year>-<6 chars>`.
 */
export const recognitionCertificateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  recognitionId: uuidSchema,
  certificateNumber: z.string(),
  issuedAt: instantSchema,
  valueName: z.string().nullable(),
  recognition: recognitionCertificateSummarySchema,
});
export type RecognitionCertificate = z.infer<typeof recognitionCertificateSchema>;

/** One emoji's tally on a post, and whether the viewer is one of the people behind it — what lets
 * the wall show "🎉 4" pressed-in for the viewer and let them take it back. */
export const recognitionReactionSummarySchema = z.object({
  emoji: z.string(),
  count: z.number().int().positive(),
  reactedByMe: z.boolean(),
});
export type RecognitionReactionSummary = z.infer<typeof recognitionReactionSummarySchema>;

/** A company value a kudos can be tagged with, e.g. "Customer first". */
export const recognitionValueSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
});
export type RecognitionValue = z.infer<typeof recognitionValueSchema>;

export const recognitionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  awardId: uuidSchema.nullable(),
  awardName: z.string().nullable(),
  awardBadgeIcon: z.string().nullable(),
  /** The company value this kudos was tagged with. Optional because the performance dashboard's
   * highlight builds this DTO from a narrower select, same as `toDepartmentName` below. */
  valueId: uuidSchema.nullable().optional(),
  valueName: z.string().nullable().optional(),
  fromPersonId: uuidSchema,
  fromName: z.string(),
  toPersonId: uuidSchema,
  toName: z.string(),
  points: pointsSchema,
  message: z.string(),
  visibility: recognitionVisibilitySchema,
  reactionCount: z.number().int().nonnegative(),
  dlpFlagged: z.boolean(),
  createdAt: instantSchema,
  /** The recipient's current department — a label for the wall, not an org-chart claim. Optional
   * because the performance dashboard's highlight builds this DTO from a narrower select. */
  toDepartmentName: z.string().nullable().optional(),
  /** Per-emoji breakdown of `reactionCount`, largest first. Present on every /recognition read and
   * reaction response; optional for the same reason as `toDepartmentName`. */
  reactions: z.array(recognitionReactionSummarySchema).optional(),
});
export type Recognition = z.infer<typeof recognitionSchema>;

/** The current (validTo: null) row — same one-row-is-the-head shape as `leaveBalanceSchema`. */
export const recognitionPointsBalanceSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  /** The fiscal-year label this row was opened under — see ../lib/fiscal.ts, never the server's
   * calendar year. */
  periodLabel: z.number().int(),
  balancePoints: z.number().int(),
  reason: z.enum(RECOGNITION_LEDGER_ENTRY_TYPES),
  validFrom: dateOnlySchema,
});
export type RecognitionPointsBalance = z.infer<typeof recognitionPointsBalanceSchema>;

/** One row of the append-only transaction ledger (R-PRF-25) — alongside, not replacing,
 * `recognitionPointsBalanceSchema` above, which stays the "current balance" head. */
export const recognitionPointsLedgerEntrySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  /** Signed — negative debits, positive credits. */
  amount: z.number().int(),
  type: z.enum(RECOGNITION_LEDGER_ENTRY_TYPES),
  balanceAfter: z.number().int(),
  recognitionId: uuidSchema.nullable(),
  redemptionId: uuidSchema.nullable(),
  /** Set only when this credit expires; null means these points never expire. */
  expiresOn: dateOnlySchema.nullable(),
  note: z.string().nullable(),
  createdAt: instantSchema,
});
export type RecognitionPointsLedgerEntry = z.infer<typeof recognitionPointsLedgerEntrySchema>;

export const rewardCatalogueItemSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  pointsCost: pointsSchema,
  /** null = unlimited. */
  stock: z.number().int().nonnegative().nullable(),
  isActive: z.boolean(),
});
export type RewardCatalogueItem = z.infer<typeof rewardCatalogueItemSchema>;

// RewardRedemptionStatus is exported from ../enums.js (REWARD_REDEMPTION_STATUSES) — not
// redeclared here, to avoid the ambiguous re-export TS2308 that a second export of the same name
// causes once both this file and enums.js are barrelled through index.ts.
export const rewardRedemptionStatusSchema = z.enum(REWARD_REDEMPTION_STATUSES);

/**
 * `providerType`/`providerReference` exist so a future external rewards provider (gift cards,
 * experiences) needs no schema change — every redemption today is `first_party`, manually
 * fulfilled by an admin. See recognition.contract.ts's header and R-PRF-25.
 */
export const rewardRedemptionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  itemId: uuidSchema,
  /** Snapshotted at request time — the catalogue item's name/cost at that moment, not necessarily
   * its current values. */
  itemName: z.string(),
  pointsCost: pointsSchema,
  status: rewardRedemptionStatusSchema,
  providerType: z.string(),
  providerReference: z.string().nullable(),
  requestedAt: instantSchema,
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  fulfilledAt: instantSchema.nullable(),
  fulfilledByName: z.string().nullable(),
  cancelReason: z.string().nullable(),
});
export type RewardRedemption = z.infer<typeof rewardRedemptionSchema>;

export const leaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  personId: uuidSchema,
  personName: z.string(),
  totalPoints: z.number().int().nonnegative(),
});
/** The caller's own recognition at a glance — the header tiles on the Recognition page. */
export const recognitionSummarySchema = z.object({
  receivedCount: z.number().int().nonnegative(),
  receivedPoints: z.number().int().nonnegative(),
  givenCount: z.number().int().nonnegative(),
  givenPoints: z.number().int().nonnegative(),
  /** Points left to give. 0 (not a 404) when HR has never allocated any. */
  balancePoints: z.number().int(),
  /** The fiscal-year label of the current balance row; null when there is none yet. */
  periodLabel: z.number().int().nullable(),
  /** Points received and not yet redeemed — the only balance a reward can be paid from. */
  redeemablePoints: z.number().int().nonnegative(),
});
export type RecognitionSummary = z.infer<typeof recognitionSummarySchema>;

export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;

export const leaderboardSchema = z.object({
  periodLabel: z.number().int().nullable(),
  entries: z.array(leaderboardEntrySchema),
});
export type Leaderboard = z.infer<typeof leaderboardSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listAwardsQuerySchema = paginationQuerySchema.extend({
  isActive: z.coerce.boolean().optional(),
});

export const createAwardBodySchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).optional(),
  points: pointsSchema,
  badgeIcon: z.string().trim().max(80).optional(),
  isActive: z.boolean().default(true),
});

export const updateAwardBodySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(1000).optional(),
  points: pointsSchema.optional(),
  badgeIcon: z.string().trim().max(80).optional(),
  isActive: z.boolean().optional(),
});

/**
 * `scope` picks the array shape; `personId` supplies its subject and defaults to the caller. Every
 * scope is NEWEST FIRST. `departmentId` narrows the wall to posts whose recipient is in that
 * department today; it is ignored for `received`/`given`, which are already one person.
 */
export const listRecognitionQuerySchema = paginationQuerySchema.extend({
  scope: z.enum(['wall', 'received', 'given']).default('wall'),
  personId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});

export const listValuesQuerySchema = z.object({
  /** Ignored for callers without `recognition:manage`, who only ever see active values. */
  isActive: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});

/** Names and descriptions are read by everyone on the picker, so they are DLP-scanned at the route. */
export const createValueBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  isActive: z.boolean().default(true),
});

export const updateValueBodySchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  isActive: z.boolean().optional(),
});

export const createRecognitionBodySchema = z.object({
  toPersonId: uuidSchema,
  awardId: uuidSchema.optional(),
  /** Optional company value to tag the kudos with. Must be an active value in this org (route-checked). */
  valueId: uuidSchema.optional(),
  /** Required when awardId is absent (a free-form kudos); ignored — the award's own points win —
   * when awardId is set. 0 is a plain thank-you: no balance needed, nothing debited. Enforced at
   * the route, not here, same as createGoalBodySchema's conditional currency pairing. */
  points: pointsSchema.optional(),
  message: z.string().trim().min(1).max(1000),
  visibility: recognitionVisibilitySchema.default('public'),
});

export const reactToRecognitionBodySchema = z.object({
  emoji: z.string().trim().min(1).max(8).default('🎉'),
});

export const removeRecognitionReactionQuerySchema = z.object({
  emoji: z.string().trim().min(1).max(8),
});

export const getPointsBalanceQuerySchema = z.object({
  personId: uuidSchema.optional(),
});

export const getLeaderboardQuerySchema = z.object({
  /** Defaults to the current fiscal-year period; 'all' sums every period on record. */
  period: z.union([z.coerce.number().int(), z.literal('all')]).default('all'),
  departmentId: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(10),
});

export const adjustPointsBalanceBodySchema = z.object({
  personId: uuidSchema,
  /** Signed — negative to debit (a correction), positive to credit (a quarterly allocation). */
  delta: z
    .number()
    .int()
    .max(100000)
    .min(-100000)
    .refine((v) => v !== 0, 'delta must not be zero'),
  reason: z.enum(['allocation', 'adjustment', 'expiry']),
  note: z.string().trim().max(500).optional(),
});

/** One recipient's line in a `bulkAllocatePoints` batch — same `personId`/`note` shape as
 * `adjustPointsBalanceBodySchema`, but `points` is unsigned: a batch only ever credits (a debit
 * stays on the single-person adjustment flow, where a human reads the negative balance warning). */
export const bulkPointsAllocationItemSchema = z.object({
  personId: uuidSchema,
  points: z.number().int().positive(),
  note: z.string().trim().max(500).optional(),
});

/**
 * `.max(100)` — same reasoning as `bulkDecideExpenseClaimsBodySchema` (expense.contract.ts): one
 * request must not become an unbounded serial walk through the whole org. Duplicate `personId`s in
 * the same batch are rejected here rather than left for the handler, since applying two close-then-
 * open writes for the same person in one transaction would race against itself.
 */
export const bulkAllocatePointsBodySchema = z
  .object({
    allocations: z.array(bulkPointsAllocationItemSchema).min(1).max(100),
  })
  .refine(
    (body) => new Set(body.allocations.map((a) => a.personId)).size === body.allocations.length,
    { message: 'Duplicate personId in the same batch', path: ['allocations'] },
  );

export const bulkAllocatePointsResultSchema = z.object({
  personId: uuidSchema,
  balancePoints: z.number().int(),
});

export const bulkAllocatePointsResponseSchema = z.object({
  results: z.array(bulkAllocatePointsResultSchema),
});

export const listLedgerQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
});

export const listRewardsQuerySchema = paginationQuerySchema.extend({
  isActive: z.coerce.boolean().optional(),
});

export const createRewardBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).optional(),
  pointsCost: pointsSchema,
  stock: z.number().int().nonnegative().nullable().optional(),
  isActive: z.boolean().default(true),
});

export const updateRewardBodySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(1000).optional(),
  pointsCost: pointsSchema.optional(),
  stock: z.number().int().nonnegative().nullable().optional(),
  isActive: z.boolean().optional(),
});

/** `scope` picks whose redemptions come back — same shape as `listRecognitionQuerySchema`.
 * `mine`: the caller's own (or, for an admin, a named `personId`'s). `queue`: every redemption an
 * admin holding `recognition:manage` may act on, across requesters — for the decide screen. */
export const listRedemptionsQuerySchema = paginationQuerySchema.extend({
  scope: z.enum(['mine', 'queue']).default('mine'),
  personId: uuidSchema.optional(),
  status: rewardRedemptionStatusSchema.optional(),
});

export const createRedemptionBodySchema = z.object({
  itemId: uuidSchema,
});

export const decideRedemptionBodySchema = z.object({
  decision: z.enum(['approve', 'cancel']),
  /** Required when declining/cancelling; ignored on approve. */
  reason: z.string().trim().max(500).optional(),
});

export const fulfillRedemptionBodySchema = z.object({
  providerReference: z.string().trim().max(200).optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const recognitionContract = c.router(
  {
    listAwards: {
      method: 'GET',
      path: '/awards',
      query: listAwardsQuerySchema,
      responses: {
        200: paginatedSchema(recognitionAwardSchema),
        401: errorBodySchema,
      },
      summary: 'List the award catalogue',
      metadata: routeMeta(null),
    },

    createAward: {
      method: 'POST',
      path: '/awards',
      body: createAwardBodySchema,
      responses: {
        201: recognitionAwardSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a catalogue award — recognition:manage',
      metadata: routeMeta('recognition:manage'),
    },

    updateAward: {
      method: 'PATCH',
      path: '/awards/:awardId',
      body: updateAwardBodySchema,
      responses: {
        200: recognitionAwardSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a catalogue award — recognition:manage',
      metadata: routeMeta('recognition:manage'),
    },

    listRecognition: {
      method: 'GET',
      path: '/',
      query: listRecognitionQuerySchema,
      responses: {
        200: paginatedSchema(recognitionSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The recognition wall, or one person's received/given kudos",
      metadata: routeMeta(null),
    },

    createRecognition: {
      method: 'POST',
      path: '/',
      body: createRecognitionBodySchema,
      responses: {
        201: recognitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Send kudos — debits the sender's points balance close-then-open and the Recognition insert in one transaction; insufficient balance is a 422",
      metadata: routeMeta('recognition:give'),
    },

    reactToRecognition: {
      method: 'POST',
      path: '/:recognitionId/react',
      body: reactToRecognitionBodySchema,
      responses: {
        200: recognitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'React to a recognition post — upsert, naturally idempotent on a double-tap',
      metadata: routeMeta(null),
    },

    removeReaction: {
      method: 'DELETE',
      path: '/:recognitionId/react',
      query: removeRecognitionReactionQuerySchema,
      body: c.type<undefined>(),
      responses: {
        200: recognitionSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "Take back the caller's own reaction to a recognition post — a no-op if they had not reacted",
      metadata: routeMeta(null),
    },

    getMySummary: {
      method: 'GET',
      path: '/summary',
      responses: {
        200: recognitionSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "The caller's recognition totals — kudos received and given (count and points) and points left to give",
      metadata: routeMeta(null),
    },

    getPointsBalance: {
      method: 'GET',
      path: '/points-balance',
      query: getPointsBalanceQuerySchema,
      responses: {
        200: recognitionPointsBalanceSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The caller's (or a visible report's) current points balance",
      metadata: routeMeta(null),
    },

    getLeaderboard: {
      method: 'GET',
      path: '/leaderboard',
      query: getLeaderboardQuerySchema,
      responses: {
        200: leaderboardSchema,
        401: errorBodySchema,
      },
      summary:
        "Top performers by points received over a period/scope — no k-anonymity suppression, deliberately (see this file's header)",
      metadata: routeMeta(null),
    },

    listLedgerEntries: {
      method: 'GET',
      path: '/points-ledger',
      query: listLedgerQuerySchema,
      responses: {
        200: paginatedSchema(recognitionPointsLedgerEntrySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The caller's (or a visible report's) points transaction history, newest first",
      metadata: routeMeta(null),
    },

    listRewards: {
      method: 'GET',
      path: '/rewards',
      query: listRewardsQuerySchema,
      responses: {
        200: paginatedSchema(rewardCatalogueItemSchema),
        401: errorBodySchema,
      },
      summary: 'List the reward catalogue',
      metadata: routeMeta(null),
    },

    listRedemptions: {
      method: 'GET',
      path: '/redemptions',
      query: listRedemptionsQuerySchema,
      responses: {
        200: paginatedSchema(rewardRedemptionSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "The caller's own redemptions, or (scope=queue) the admin decide queue",
      metadata: routeMeta(null),
    },

    // GET '/:recognitionId' MUST be registered after every static single-segment GET path above
    // ('/awards', '/', '/summary', '/points-balance', '/leaderboard', '/points-ledger', '/rewards',
    // '/redemptions' — the last three added by R-PRF-25) — same route-ordering convention as
    // feedback.contract.ts's `getFeedback`; see that file's comment for the full reasoning.
    // Static GET '/values' must sit above getRecognition's '/:recognitionId', same as '/awards' above.
    listValues: {
      method: 'GET',
      path: '/values',
      query: listValuesQuerySchema,
      responses: {
        200: z.object({ items: z.array(recognitionValueSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List company values — anyone who can give recognition; inactive ones are manager-only',
      metadata: routeMeta('recognition:give'),
    },

    createValue: {
      method: 'POST',
      path: '/values',
      body: createValueBodySchema,
      responses: {
        201: recognitionValueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a company value — recognition:manage',
      metadata: routeMeta('recognition:manage'),
    },

    updateValue: {
      method: 'PATCH',
      path: '/values/:valueId',
      body: updateValueBodySchema,
      responses: {
        200: recognitionValueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a company value — recognition:manage',
      metadata: routeMeta('recognition:manage'),
    },

    deactivateValue: {
      method: 'POST',
      path: '/values/:valueId/deactivate',
      body: c.noBody(),
      responses: {
        200: recognitionValueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Stop offering a company value on new kudos; past kudos keep it — recognition:manage, idempotent',
      metadata: routeMeta('recognition:manage'),
    },

    getRecognition: {
      method: 'GET',
      path: '/:recognitionId',
      responses: {
        200: recognitionSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one recognition entry',
      metadata: routeMeta(null),
    },

    adjustPointsBalance: {
      method: 'POST',
      path: '/points-balance/adjust',
      body: adjustPointsBalanceBodySchema,
      responses: {
        200: recognitionPointsBalanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'HR adjustment or quarterly allocation — recognition:manage, close-then-open',
      metadata: routeMeta('recognition:manage'),
    },

    bulkAllocatePoints: {
      method: 'POST',
      path: '/points-balance/bulk-allocate',
      body: bulkAllocatePointsBodySchema,
      responses: {
        200: bulkAllocatePointsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Atomic bulk quarterly allocation across recipients — recognition:manage, all-or-nothing, close-then-open per recipient',
      metadata: routeMeta('recognition:manage'),
    },

    // ---------------------------------------------------------------------
    // Redeemable points (R-PRF-25)
    // ---------------------------------------------------------------------

    createReward: {
      method: 'POST',
      path: '/rewards',
      body: createRewardBodySchema,
      responses: {
        201: rewardCatalogueItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Create a reward catalogue item — recognition:manage',
      metadata: routeMeta('recognition:manage'),
    },

    updateReward: {
      method: 'PATCH',
      path: '/rewards/:itemId',
      body: updateRewardBodySchema,
      responses: {
        200: rewardCatalogueItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a reward catalogue item — recognition:manage',
      metadata: routeMeta('recognition:manage'),
    },

    // GET '/redemptions/:redemptionId' MUST be registered after 'listRedemptions' above — same
    // route-ordering convention as getRecognition.
    createRedemption: {
      method: 'POST',
      path: '/redemptions',
      body: createRedemptionBodySchema,
      responses: {
        201: rewardRedemptionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Request a redemption — status starts at pending; the ledger is NOT debited yet, only on fulfilment',
      metadata: routeMeta('recognition:give'),
    },

    getRedemption: {
      method: 'GET',
      path: '/redemptions/:redemptionId',
      responses: {
        200: rewardRedemptionSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one redemption',
      metadata: routeMeta(null),
    },

    decideRedemption: {
      method: 'POST',
      path: '/redemptions/:redemptionId/decide',
      body: decideRedemptionBodySchema,
      responses: {
        200: rewardRedemptionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Approve (pending -> approved) or cancel (pending/approved -> cancelled) — recognition:manage, no ledger effect either way',
      metadata: routeMeta('recognition:manage'),
    },

    fulfillRedemption: {
      method: 'POST',
      path: '/redemptions/:redemptionId/fulfill',
      body: fulfillRedemptionBodySchema,
      responses: {
        200: rewardRedemptionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'approved -> fulfilled — recognition:manage; the ONE point in the whole flow that debits the ledger and balance',
      metadata: routeMeta('recognition:manage'),
    },
  },
  { pathPrefix: '/recognition' },
);

// ---------------------------------------------------------------------------
// Certificates — a separate router so it mounts in its own route file
// (apps/api/src/routes/recognition-certificates.ts). Same `/recognition` prefix.
// ---------------------------------------------------------------------------

export const recognitionCertificateContract = c.router(
  {
    issueCertificate: {
      method: 'POST',
      path: '/:recognitionId/certificate',
      body: c.noBody(),
      responses: {
        /** Created now. */
        201: recognitionCertificateSchema,
        /** Already issued: the existing certificate, unchanged. */
        200: recognitionCertificateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Issue a certificate for one kudos on request. Allowed for the giver, the recipient, or anyone with recognition:manage over the recipient. A second call returns the existing certificate',
      metadata: routeMeta(null),
    },

    getCertificate: {
      method: 'GET',
      path: '/:recognitionId/certificate',
      responses: {
        200: recognitionCertificateSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get the certificate for one kudos. Same visibility as issuing. 404 until issued',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/recognition' },
);
